import { api } from '@/lib/api/client';
import type {
  Matter,
  MatterListResponse,
  MatterFilters,
  CreateMatterDto,
  UpdateMatterDto,
  MatterResult,
  MatterPhase,
  GateCheckResult,
  LeadInfo,
  CreateQuickMatterDto,
  ComplianceState,
  SetComplianceDto,
  SetRiskLevelDto,
  BypassRateInfo,
  RiskLevel,
  CloseSuspendedDto,
  InteractionEntry,
  CreateInteractionDto,
  UpdateInteractionDto,
  CourtInstance,
  CreateCourtInstanceDto,
  UpdateCourtInstanceDto,
  CaseInstance,
  StartCaseInstanceDto,
  StopCaseInstanceDto,
  UpdateCaseInstanceDto,
  MatterDocument,
  CreateDocumentDto,
  UpdateDocumentDto,
  BreakGlassRequestDto,
  GrantBreakGlassDto,
  ChangeRequest,
  CreateChangeRequestDto,
  UpdateMatterPlanDto,
  MatterPlan,
  MatterTeam,
  QaCriterion,
  MatterQaScore,
  SubmitMatterQaDto,
} from '../types';

// Query keys for React Query
export const matterKeys = {
  all: ['matters'] as const,
  lists: () => [...matterKeys.all, 'list'] as const,
  list: (filters: MatterFilters) => [...matterKeys.lists(), filters] as const,
  details: () => [...matterKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...matterKeys.details(), id] as const,
  gateCheck: (id: string | number, to?: string) =>
    [...matterKeys.all, 'gate-check', id, to ?? 'next'] as const,
  lead: (id: string | number) => [...matterKeys.all, 'lead', id] as const,
  team: (id: string | number) => [...matterKeys.all, 'team', id] as const,
  compliance: (id: string | number) =>
    [...matterKeys.all, 'compliance', id] as const,
  bypassRate: () => [...matterKeys.all, 'bypass-rate'] as const,
  interactions: (id: string | number) =>
    [...matterKeys.all, 'interactions', id] as const,
  unfilledInteractions: () =>
    [...matterKeys.all, 'interactions-unfilled'] as const,
  instances: (id: string | number) =>
    [...matterKeys.all, 'instances', id] as const,
  caseInstances: (id: string | number) =>
    [...matterKeys.all, 'case-instances', id] as const,
  documents: (id: string | number) =>
    [...matterKeys.all, 'documents', id] as const,
  document: (documentId: number) =>
    [...matterKeys.all, 'document', documentId] as const,
  changeRequests: (id: string | number) =>
    [...matterKeys.all, 'change-requests', id] as const,
  qa: (id: string | number) => [...matterKeys.all, 'qa', id] as const,
  qaCriteria: () => [...matterKeys.all, 'qa-criteria'] as const,
};

// Build params for list query
function buildListParams(filters: MatterFilters): Record<string, unknown> {
  const offset = (filters.page ?? 0) * (filters.limit ?? 10);
  const params: Record<string, unknown> = {
    limit: filters.limit ?? 10,
    offset,
  };

  // Search
  if (filters.s && filters.s.length >= 2) {
    params['s'] = filters.s;
  }

  // Sorting
  if (filters.orderBy && filters.order) {
    params['orderBy'] = filters.orderBy;
    params['order'] = filters.order;
  }

  // Lifecycle phase (the legacy status filter is gone)
  if (filters.phase) {
    params['phase'] = filters.phase;
  }

  // Multi-phase filter (comma-separated) — takes precedence over `phase`
  if (filters.phases) {
    params['phases'] = filters.phases;
  }

  // Result
  if (filters.result) {
    params['result'] = filters.result;
  }

  // Compliance decision
  if (filters.compliance) {
    params['compliance'] = filters.compliance;
  }

  // Relations
  if (filters.clientId) params['clientId'] = filters.clientId;
  if (filters.userId) params['userId'] = filters.userId;
  if (filters.contractId) params['contractId'] = filters.contractId;
  if (filters.practiceAreaId) params['practiceAreaId'] = filters.practiceAreaId;
  // Boolean filters
  if (filters.isCourtCase !== undefined) {
    params['isCourtCase'] = filters.isCourtCase;
  }

  // Date filters
  if (filters['dueAt.min']) {
    params['dueAt.min'] = new Date(filters['dueAt.min']).toISOString();
  }
  if (filters['dueAt.max']) {
    params['dueAt.max'] = new Date(filters['dueAt.max']).toISOString();
  }

  return params;
}

// API functions
export const mattersApi = {
  // List matters with filters
  getMatters: async (filters: MatterFilters): Promise<MatterListResponse> => {
    const params = buildListParams(filters);
    const response = await api.get<MatterListResponse>('/matters', { params });
    return response;
  },

  // Get single matter
  getMatter: async (id: string | number): Promise<Matter> => {
    const response = await api.get<Matter>(`/matters/${id}`);
    return response;
  },

  // Create matter
  createMatter: async (data: CreateMatterDto): Promise<Matter> => {
    const response = await api.post<Matter>('/matters', data);
    return response;
  },

  // Update matter
  updateMatter: async ({ id, data }: { id: string | number; data: UpdateMatterDto }): Promise<Matter> => {
    const response = await api.patch<Matter>(`/matters/${id}`, data);
    return response;
  },

  // Delete matter
  deleteMatter: async (id: string | number): Promise<void> => {
    await api.delete(`/matters/${id}`);
  },

  // Close matter with result (softOverrideReason audits a Gate-4 soft bypass)
  closeMatter: async (
    id: string | number,
    result: MatterResult = 'LOSE',
    softOverrideReason?: string,
  ): Promise<Matter> => {
    const response = await api.put<Matter>(`/matters/close/${id}`, {
      result,
      ...(softOverrideReason ? { softOverrideReason } : {}),
    });
    return response;
  },

  // Submit NPS rating for a closed matter (K2)
  submitNps: async (id: string | number, nps: number): Promise<Matter> => {
    const response = await api.patch<Matter>(`/matters/closed/${id}`, { nps });
    return response;
  },

  // Case 360° — preview the Quality Gate for a forward transition
  // (defaults to the legal next phase when `to` is omitted).
  gateCheck: async (
    id: string | number,
    to?: MatterPhase,
  ): Promise<GateCheckResult> => {
    const response = await api.get<GateCheckResult>(`/matters/${id}/gate-check`, {
      params: to ? { to } : {},
    });
    return response;
  },

  // Case 360° — move a matter forward through its Quality Gate (forward-only)
  transition: async (
    id: string | number,
    toPhase: MatterPhase,
    softOverrideReason?: string,
  ): Promise<{ id: number; fromPhase: MatterPhase; toPhase: MatterPhase; gate: number | null }> => {
    const response = await api.post(`/matters/${id}/transition`, {
      toPhase,
      ...(softOverrideReason ? { softOverrideReason } : {}),
    });
    return response;
  },

  // Case 360° (Stage 2) — LACI Lead
  getLead: async (id: string | number): Promise<LeadInfo> => {
    const response = await api.get<LeadInfo>(`/matters/${id}/lead`);
    return response;
  },

  // Propose a Lead handoff (stays pending until the new Lead acknowledges)
  delegateLead: async (id: string | number, toUserId: number): Promise<void> => {
    await api.post(`/matters/${id}/lead/delegate`, { toUserId });
  },

  // Incoming Lead confirms — responsibility transfers now
  acknowledgeLead: async (id: string | number): Promise<void> => {
    await api.post(`/matters/${id}/lead/acknowledge`, {});
  },

  // Partner/MD assigns a Lead immediately (no acknowledge)
  forceAssignLead: async (id: string | number, toUserId: number): Promise<void> => {
    await api.post(`/matters/${id}/lead/force-assign`, { toUserId });
  },

  // Case 360° (Stage 4) — Quick Advice (QA)
  // 3-click create: Topic + Contract + Responsible Partner
  createQuick: async (data: CreateQuickMatterDto): Promise<Matter> => {
    const response = await api.post<Matter>('/matters/quick', data);
    return response;
  },

  // Extend the 7-day deadline by 7 days (max 2 extensions)
  extendQuick: async (id: string | number): Promise<Matter> => {
    const response = await api.post<Matter>(`/matters/${id}/quick/extend`, {});
    return response;
  },

  // Convert a QA matter into a full matter (→ PLANNING, Gate 1 passed)
  convertQuick: async (id: string | number, note?: string): Promise<Matter> => {
    const response = await api.post<Matter>(`/matters/${id}/quick/convert`, {
      ...(note ? { note } : {}),
    });
    return response;
  },

  // Case 360° (Stage 14) — the Team tab read model (server-owned structure)
  getTeam: async (id: string | number): Promise<MatterTeam> => {
    return api.get<MatterTeam>(`/matters/${id}/team`);
  },

  // Case 360° (Stage 14) — inline team management (members display)
  // Partner/MD swaps the matter Partner (Accountable)
  reassignPartner: async (
    id: string | number,
    data: { fromUserId?: number | undefined; toUserId: number },
  ): Promise<void> => {
    await api.post(`/matters/${id}/partner`, data);
  },

  // Lead/Partner/MD adds a Contributor (staff- or lead-type user)
  addStaff: async (id: string | number, userId: number): Promise<void> => {
    await api.post(`/matters/${id}/staff`, { userId });
  },

  // Lead/Partner/MD removes a Contributor (work-preserving cleanup server-side)
  removeStaff: async (id: string | number, userId: number): Promise<void> => {
    await api.delete(`/matters/${id}/staff/${userId}`);
  },

  // Lead/Partner/MD replaces a Contributor — active tasks transfer to the
  // incoming staffer (prior work preserved as finished 0% adjustments,
  // running timers stopped automatically server-side)
  reassignStaff: async (
    id: string | number,
    fromUserId: number,
    toUserId: number,
  ): Promise<{ reassignedTasks: number }> => {
    return api.post<{ reassignedTasks: number }>(
      `/matters/${id}/staff/reassign`,
      { fromUserId, toUserId },
    );
  },

  // Case 360° (Stage 14) — Matter Report export (generated fresh per call)
  downloadReport: async (
    id: string | number,
    format: 'pdf' | 'docx',
  ): Promise<Blob> => {
    return api.get<Blob>(`/matters/${id}/report`, {
      params: { format },
      responseType: 'blob',
    });
  },

  // Case 360° (Stage 6) — Compliance, Risk & BYPASS control
  getCompliance: async (id: string | number): Promise<ComplianceState> => {
    const response = await api.get<ComplianceState>(`/matters/${id}/compliance`);
    return response;
  },

  // Partner sets the Compliance decision (+ optional Risk Level)
  setCompliance: async (
    id: string | number,
    data: SetComplianceDto,
  ): Promise<ComplianceState> => {
    const response = await api.post<ComplianceState>(
      `/matters/${id}/compliance`,
      data,
    );
    return response;
  },

  // Update Risk Level alone (does not re-trigger Gate 1)
  setRiskLevel: async (
    id: string | number,
    data: SetRiskLevelDto,
  ): Promise<{ id: number; riskLevel: RiskLevel }> => {
    const response = await api.patch(`/matters/${id}/risk-level`, data);
    return response;
  },

  // Firm-wide BYPASS rate (rolling 30 days) + alert level + MD override
  getBypassRate: async (): Promise<BypassRateInfo> => {
    const response = await api.get<BypassRateInfo>(
      '/matters/compliance/bypass-rate',
    );
    return response;
  },

  // MD lifts/re-applies the >10% BYPASS auto-block
  setBypassOverride: async (mdOverride: boolean): Promise<BypassRateInfo> => {
    const response = await api.post<BypassRateInfo>(
      '/matters/compliance/bypass-override',
      { mdOverride },
    );
    return response;
  },

  // Case 360° (Stage 5) — Emergency Mode & SUSPENDED
  // INTAKE → PROVISIONAL_OPEN (48h window, billing tracking-only)
  emergencyOpen: async (id: string | number): Promise<Matter> => {
    const response = await api.post<Matter>(`/matters/${id}/emergency-open`, {});
    return response;
  },

  // Extend the emergency window by 48h (max 2 extensions)
  emergencyExtend: async (id: string | number): Promise<Matter> => {
    const response = await api.post<Matter>(`/matters/${id}/emergency-extend`, {});
    return response;
  },

  // PROVISIONAL_OPEN → PLANNING once Compliance passed
  emergencyResolve: async (id: string | number): Promise<Matter> => {
    const response = await api.post<Matter>(`/matters/${id}/emergency-resolve`, {});
    return response;
  },

  // Archive a SUSPENDED matter (CLOSED / CANCELLED) with closure type + reason
  closeSuspended: async (
    id: string | number,
    data: CloseSuspendedDto,
  ): Promise<Matter> => {
    const response = await api.post<Matter>(`/matters/${id}/suspended-close`, data);
    return response;
  },

  // Case 360° (Stage 7) — Interaction Log
  getInteractions: async (id: string | number): Promise<InteractionEntry[]> => {
    const response = await api.get<InteractionEntry[]>(
      `/matters/${id}/interactions`,
    );
    return response;
  },

  // Log an interaction (author + matter auto-filled server-side)
  createInteraction: async (
    id: string | number,
    data: CreateInteractionDto,
  ): Promise<InteractionEntry> => {
    const response = await api.post<InteractionEntry>(
      `/matters/${id}/interactions`,
      data,
    );
    return response;
  },

  // Fill/edit an entry; completing the summary finalizes a draft
  updateInteraction: async (
    entryId: number,
    data: UpdateInteractionDto,
  ): Promise<InteractionEntry> => {
    const response = await api.patch<InteractionEntry>(
      `/matters/interactions/${entryId}`,
      data,
    );
    return response;
  },

  // Grouped reminder: how many of MY drafts still need a summary
  getUnfilledInteractionCount: async (): Promise<{ count: number }> => {
    const response = await api.get<{ count: number }>(
      '/matters/interactions/unfilled-count',
    );
    return response;
  },

  // Case 360° (Stage 9) — Court Instances & Appeal Deadlines
  getCourtInstances: async (id: string | number): Promise<CourtInstance[]> => {
    const response = await api.get<CourtInstance[]>(`/matters/${id}/instances`);
    return response;
  },

  // Open a court instance (manual appeal deadline)
  createCourtInstance: async (
    id: string | number,
    data: CreateCourtInstanceDto,
  ): Promise<CourtInstance> => {
    const response = await api.post<CourtInstance>(
      `/matters/${id}/instances`,
      data,
    );
    return response;
  },

  // Update an instance; changing a set deadline requires overrideReason
  updateCourtInstance: async (
    instanceId: number,
    data: UpdateCourtInstanceDto,
  ): Promise<CourtInstance> => {
    const response = await api.patch<CourtInstance>(
      `/matters/instances/${instanceId}`,
      data,
    );
    return response;
  },

  // Case Instances — named work engagements on INSTANCE-billed matters
  getCaseInstances: async (id: string | number): Promise<CaseInstance[]> => {
    const response = await api.get<CaseInstance[]>(
      `/matters/${id}/case-instances`,
    );
    return response;
  },

  startCaseInstance: async (
    id: string | number,
    data: StartCaseInstanceDto,
  ): Promise<CaseInstance> => {
    const response = await api.post<CaseInstance>(
      `/matters/${id}/case-instances/start`,
      data,
    );
    return response;
  },

  stopCaseInstance: async (
    id: string | number,
    data: StopCaseInstanceDto,
  ): Promise<CaseInstance> => {
    const response = await api.post<CaseInstance>(
      `/matters/${id}/case-instances/stop`,
      data,
    );
    return response;
  },

  updateCaseInstance: async (
    id: string | number,
    instanceId: number,
    data: UpdateCaseInstanceDto,
  ): Promise<CaseInstance> => {
    const response = await api.patch<CaseInstance>(
      `/matters/${id}/case-instances/${instanceId}`,
      data,
    );
    return response;
  },

  // Case 360° (Stage 10) — Documents (versioning, SECRET, break-glass)
  getDocuments: async (matterId: string | number): Promise<MatterDocument[]> => {
    const response = await api.get<MatterDocument[]>('/documents', {
      params: { matterId },
    });
    return response;
  },

  // Detail with the immutable version history (SECRET-gated)
  getDocument: async (documentId: number): Promise<MatterDocument> => {
    const response = await api.get<MatterDocument>(`/documents/${documentId}`);
    return response;
  },

  // Create a document — the uploaded file becomes version 1
  createDocument: async (data: CreateDocumentDto): Promise<MatterDocument> => {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('matterId', String(data.matterId));
    if (data.name) formData.append('name', data.name);
    if (data.isSecret) formData.append('isSecret', 'true');
    if (data.isEngagementLetter) formData.append('isEngagementLetter', 'true');
    const response = await api.post<MatterDocument>('/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response;
  },

  // Download a version's file (streamed from MinIO through the authorized API)
  downloadDocumentVersion: async (
    documentId: number,
    versionId: number,
  ): Promise<Blob> => {
    return api.get<Blob>(
      `/documents/${documentId}/versions/${versionId}/download`,
      { responseType: 'blob' },
    );
  },

  // Upload a new version (appended to the history, becomes current)
  addDocumentVersion: async (
    documentId: number,
    file: File,
  ): Promise<MatterDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<MatterDocument>(
      `/documents/${documentId}/versions`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response;
  },

  // Rename / toggle SECRET (the flag: Lead/Partner/MD only)
  updateDocument: async (
    documentId: number,
    data: UpdateDocumentDto,
  ): Promise<MatterDocument> => {
    const response = await api.patch<MatterDocument>(
      `/documents/${documentId}`,
      data,
    );
    return response;
  },

  // Rollback — make a prior version current again (no re-upload)
  rollbackDocument: async (
    documentId: number,
    versionId: number,
  ): Promise<MatterDocument> => {
    const response = await api.post<MatterDocument>(
      `/documents/${documentId}/rollback`,
      { versionId },
    );
    return response;
  },

  // Soft-delete (Lead/Partner/MD only)
  deleteDocument: async (documentId: number): Promise<MatterDocument> => {
    const response = await api.delete<MatterDocument>(
      `/documents/${documentId}`,
    );
    return response;
  },

  // Break-glass: ask the matter Partner(s)/MD for access to a SECRET document
  requestBreakGlass: async (
    documentId: number,
    data: BreakGlassRequestDto,
  ): Promise<{ status: string }> => {
    const response = await api.post<{ status: string }>(
      `/documents/${documentId}/break-glass-request`,
      data,
    );
    return response;
  },

  // Break-glass: grant a named auditor 4h of access (MD / matter partner)
  grantBreakGlass: async (
    documentId: number,
    data: GrantBreakGlassDto,
  ): Promise<unknown> => {
    const response = await api.post<unknown>(
      `/documents/${documentId}/break-glass`,
      data,
    );
    return response;
  },

  // Break-glass: deny a pending request (MD / matter partner)
  denyBreakGlass: async (
    documentId: number,
    data: { auditorId: number },
  ): Promise<unknown> => {
    const response = await api.post<unknown>(
      `/documents/${documentId}/break-glass-deny`,
      data,
    );
    return response;
  },

  // Case 360° (Stage 11) — Matter Plan & Change Requests
  // Free-edit of the plan fields (only pre-Gate-2; later — via a CR)
  updatePlan: async (
    id: string | number,
    data: UpdateMatterPlanDto,
  ): Promise<MatterPlan> => {
    const response = await api.patch<MatterPlan>(`/matters/${id}/plan`, data);
    return response;
  },

  // Lead/Partner sign-off on the plan (Gate 2 soft check)
  confirmPlan: async (id: string | number): Promise<MatterPlan> => {
    const response = await api.post<MatterPlan>(
      `/matters/${id}/plan/confirm`,
      {},
    );
    return response;
  },

  // Mark Client Feedback as requested (Gate 4 soft check)
  requestFeedback: async (id: string | number): Promise<MatterPlan> => {
    const response = await api.post<MatterPlan>(
      `/matters/${id}/feedback-request`,
      {},
    );
    return response;
  },

  // List a matter's change requests (newest first)
  getChangeRequests: async (id: string | number): Promise<ChangeRequest[]> => {
    const response = await api.get<ChangeRequest[]>(
      `/matters/${id}/change-requests`,
    );
    return response;
  },

  // Submit a CR (SCOPE / BUDGET / TIMELINE / TEAM) — stays PENDING
  createChangeRequest: async (
    id: string | number,
    data: CreateChangeRequestDto,
  ): Promise<ChangeRequest> => {
    const response = await api.post<ChangeRequest>(
      `/matters/${id}/change-requests`,
      data,
    );
    return response;
  },

  // Approve — the target field is updated only here
  approveChangeRequest: async (crId: number): Promise<ChangeRequest> => {
    const response = await api.post<ChangeRequest>(
      `/matters/change-requests/${crId}/approve`,
      {},
    );
    return response;
  },

  // Reject with a reason — nothing changes
  rejectChangeRequest: async (
    crId: number,
    rejectReason: string,
  ): Promise<ChangeRequest> => {
    const response = await api.post<ChangeRequest>(
      `/matters/change-requests/${crId}/reject`,
      { rejectReason },
    );
    return response;
  },

  // ── Matter QA scoring — the K1 (Quality_Index) source ─────────────────────
  // Reviewer assessment on a CLOSED matter, per criterion, 0–5. The server
  // enforces the seniority matrix (MD → anyone, Partner → Lead + Contributors,
  // Lead → Contributors) and rejects self-rating.

  /** The MATTER criteria matrix a QA score is given against. */
  getQaCriteria: async (): Promise<{ results?: QaCriterion[] } | QaCriterion[]> => {
    const response = await api.get<{ results?: QaCriterion[] } | QaCriterion[]>(
      '/qa-criteria/select-box',
      { params: { type: 'MATTER', limit: 100 } },
    );
    return response;
  },

  /** Scores already recorded on this matter (any rater, any rated user). */
  getQa: async (id: string | number): Promise<MatterQaScore[]> => {
    const response = await api.get<MatterQaScore[]>(`/matters/${id}/qa`);
    return response;
  },

  /** Upsert this rater's scores — re-submitting overwrites their own entries. */
  submitQa: async (
    id: string | number,
    data: SubmitMatterQaDto,
  ): Promise<void> => {
    await api.post<void>(`/matters/${id}/qa`, data);
  },
};
